import * as React from 'react';
import { inject, observer } from 'mobx-react';
import * as styles from "./Panel.css";
import { OrgAssetsAdminStore } from '../stores';
import { IAPanel } from '../../panels/Panel';
import { IATextField } from '../../textField/TextField';
import { IAButton } from '../../button/Button';
import OrgAssetsService from '../../../services/OrgAssetsService';
import { IOrgAssetAddRequest } from '../../../interfaces/OrgAssets/IOrgAssetAddRequest';
import { CdnTypeEnum } from '../../../interfaces/OrgAssets/CdnTypeEnum';
import { IOrgAssetUpdateRequest } from '../../../interfaces/OrgAssets/IOrgAssetUpdateRequest';
import { IAMessageBar } from '../../messageBar/MessageBar';
import { LocalizationService } from '../../../services/LocalizationService';
import { IADropDown } from '../../dropDown/DropDown';
import { OrgAssetTypeEnum } from '../../../interfaces/OrgAssets/OrgAssetTypeEnum';

export interface IViewPanelProps {
  orgAssetsAdminStore?: OrgAssetsAdminStore;
}

@inject('orgAssetsAdminStore')
@observer
export default class ViewPanel extends React.Component<IViewPanelProps> {

  constructor(props) {
    super(props);
  }

  private async save(): Promise<void> {
    this.props.orgAssetsAdminStore.saving = true;
    // ThumbnailUrl is optional - if not set it has to be null
    let thumbnailUrl = this.props.orgAssetsAdminStore.orgAssetSelected.thumbnailUrl;
    thumbnailUrl = thumbnailUrl?.decodedUrl ? thumbnailUrl : null;
    if (this.props.orgAssetsAdminStore.isAddMode) {
      // Add request
      const item: IOrgAssetAddRequest = {
        site: this.props.orgAssetsAdminStore.orgAssets.url ?
          this.props.orgAssetsAdminStore.orgAssets.url.decodedUrl : this.getSiteFromUrl(this.props.orgAssetsAdminStore.orgAssetSelected.libraryUrl.decodedUrl),
        title: this.props.orgAssetsAdminStore.orgAssetSelected?.displayName,
        libUrl: this.props.orgAssetsAdminStore.orgAssetSelected.libraryUrl,
        thumbnailUrl: thumbnailUrl,
        cdnType: CdnTypeEnum.Private,
        orgAssetType: this.props.orgAssetsAdminStore.orgAssetSelected.orgAssetType
      };
      let addOrgAssetResponse = await OrgAssetsService.addOrgAsset(this.props.orgAssetsAdminStore.environment,
        this.props.orgAssetsAdminStore.token, this.props.orgAssetsAdminStore.tenant, item);
      this.props.orgAssetsAdminStore.setErrorMessage(addOrgAssetResponse);
    }
    else if (this.props.orgAssetsAdminStore.isEditMode) {
      // Update request
      const item: IOrgAssetUpdateRequest = {
        libUrl: this.props.orgAssetsAdminStore.orgAssetSelected.libraryUrl,
        thumbnailUrl: thumbnailUrl
      };
      let updateOrgAssetResponse = await OrgAssetsService.updateOrgAsset(this.props.orgAssetsAdminStore.environment,
        this.props.orgAssetsAdminStore.token, this.props.orgAssetsAdminStore.tenant, this.props.orgAssetsAdminStore.orgAssetSelected.id, item);
      this.props.orgAssetsAdminStore.setErrorMessage(updateOrgAssetResponse);
    }
    this.props.orgAssetsAdminStore.saving = false;
  }

  private ensureRelativeAndDecodedUrl(url: string): string {
    url = decodeURI(url);
    return url.replace(`https://${this.props.orgAssetsAdminStore.tenant}.sharepoint.com`, '');
  }

  private splitRelativeUrl(url: string, splitLimit: number): string {
    url = this.ensureRelativeAndDecodedUrl(url);
    if (url.indexOf("/sites/") === -1) {
      splitLimit--;
    }
    return url.split('/', splitLimit).join('/');
  }

  private getSiteFromUrl(url: string): string {
    const site = this.splitRelativeUrl(url, 3);
    return site;
  }

  private getLibraryUrl(url: string): string {
    const libraryUrl = this.splitRelativeUrl(url, 4);
    return libraryUrl;
  }

  public render(): JSX.Element {
    const showPanel: boolean = !!this.props.orgAssetsAdminStore.orgAssetSelected && !this.props.orgAssetsAdminStore.isDeleteMode;
    let content: JSX.Element;
    if (showPanel) {
      content = (
        <div className={styles.viewPanelContentWrapper}>
          <div className={styles.section}>
            {this.props.orgAssetsAdminStore.isAddMode &&
              <IADropDown
                label={new LocalizationService().strings.OrgAssetsAdmin_SelectLibraryType}
                selectedOption={this.props.orgAssetsAdminStore.orgAssetSelected.orgAssetType}
                options={[
                  {
                    key: OrgAssetTypeEnum.ImageDocumentLibrary.toString(),
                    value: new LocalizationService().strings.OrgAssetsAdmin_ImageDocumentLibrary
                  },
                  {
                    key: OrgAssetTypeEnum.OfficeTemplateLibrary.toString(),
                    value: new LocalizationService().strings.OrgAssetsAdmin_OfficeTemplateLibrary
                  }
                ]}
                onChange={(value: OrgAssetTypeEnum) => {
                  this.props.orgAssetsAdminStore.orgAssetSelected.orgAssetType = value;
                }}
              />
            }
            <IATextField
              label={new LocalizationService().strings.OrgAssetsAdmin_Field_LibraryName}
              placeholder={new LocalizationService().strings.OrgAssetsAdmin_Field_LibraryName_Placeholder}
              text={this.props.orgAssetsAdminStore.orgAssetSelected ? this.props.orgAssetsAdminStore.orgAssetSelected.displayName : ""}
              onChange={(value: string) => {
                this.props.orgAssetsAdminStore.orgAssetSelected.displayName = value;
              }}
              disabled={this.props.orgAssetsAdminStore.isEditMode}
            />
            <IATextField
              label={new LocalizationService().strings.OrgAssetsAdmin_Field_LibraryUrl}
              placeholder={new LocalizationService().strings.OrgAssetsAdmin_Field_LibraryUrl_Placeholder}
              text={this.props.orgAssetsAdminStore.orgAssetSelected ?
                this.getLibraryUrl(this.props.orgAssetsAdminStore.orgAssetSelected.libraryUrl.decodedUrl) : ""}
              onChange={(value: string) => {
                this.props.orgAssetsAdminStore.orgAssetSelected.libraryUrl.decodedUrl = value;
              }}
            />
            <IATextField
              label={new LocalizationService().strings.OrgAssetsAdmin_Field_ThumbnailUrl}
              placeholder={new LocalizationService().strings.OrgAssetsAdmin_Field_ThumbnailUrl_Placeholder}
              text={this.props.orgAssetsAdminStore.orgAssetSelected?.thumbnailUrl ?
                this.ensureRelativeAndDecodedUrl(this.props.orgAssetsAdminStore.orgAssetSelected.thumbnailUrl.decodedUrl) : ""}
              onChange={(value: string) => {
                this.props.orgAssetsAdminStore.orgAssetSelected.thumbnailUrl = value ?
                  {
                    decodedUrl: value
                  }
                  :
                  null;
              }}
            />
          </div>
          {this.props.orgAssetsAdminStore.errorMessage &&
            <IAMessageBar
              icon="Error"
              showIcon={true}
              type="error"
              content={this.props.orgAssetsAdminStore.errorMessage}
              onDismiss={() => { }}
              style={{ position: "absolute", bottom: "60px", marginBottom: "20px", padding: "0px 10px", justifyContent: "left" }}
            />
          }
          <IAButton
            label={new LocalizationService().strings.Messages_EditPanel_SaveAndClose}
            disbaled={
              !this.props.orgAssetsAdminStore.orgAssetSelected.displayName ||
              !this.props.orgAssetsAdminStore.orgAssetSelected.libraryUrl.decodedUrl ||
              !this.props.orgAssetsAdminStore.isChanged()
            }
            buttonColor="#02a5e2"
            onClick={async () => {
              await this.save();
              if (!this.props.orgAssetsAdminStore.errorMessage) {
                localStorage.removeItem("IntraActive_OrgAsset_Selected");
                this.props.orgAssetsAdminStore.orgAssetSelected = undefined;
                await this.props.orgAssetsAdminStore.getOrgAssets();
              }
            }}
            showSpinner={this.props.orgAssetsAdminStore.saving}
            dataAutomationId="orgassets-panel-save"
            style={{
              position: "absolute",
              bottom: 30,
              right: 30
            }}
          />
        </div>
      );
    }
    return (
      <div>
        <IAPanel
          showNavigationBar={true}
          open={showPanel}
          transition={this.props.orgAssetsAdminStore.orgAssetSelected != undefined ? "fade" : "slide"}
          width={550}
          isMobile={false}
          close={() => {
            if (this.props.orgAssetsAdminStore.isAddMode == false) {
              // Rollback changes to org assets item
              this.props.orgAssetsAdminStore.reset();
            }
            this.props.orgAssetsAdminStore.orgAssetSelected = undefined;
            this.props.orgAssetsAdminStore.errorMessage = undefined;
          }}
        >{content}</IAPanel>
      </div>
    );
  }

  
}